/**
 * attachments — the scans on a credential, as Symfony serves them.
 *
 * Every certificate, document, endorsement and flight review lives in a
 * PRIVATE Spaces bucket. The API lists each scan with a presigned `url` (and
 * `thumbnailUrl` for images) that works for 15 minutes with no auth header
 * (symphony-holdshort `docs/api/private-attachments.md`). The old
 * `images[]`/`files[]` are bare storage paths no endpoint serves, so they are
 * never loaded here.
 *
 *   record.attachments[]          certificates, documents, flight reviews
 *   record.attachmentDetails      an endorsement type (one scan, or null)
 *
 * Normalised item: `{ id, kind: 'image'|'file', name, url, thumbnailUrl, expiresAt }`.
 * `url: null` means storage could not sign it — show it as unavailable.
 */

const RENEW_MARGIN_MS = 60 * 1000;

function toItem(a) {
	if (!a || typeof a !== 'object') return null;
	const kind = a.type === 'image' ? 'image' : 'file';
	return {
		id: a.id != null ? a.id : null,
		kind,
		name: a.name || (kind === 'image' ? 'Image' : 'File'),
		url: a.url || null,
		thumbnailUrl: kind === 'image' ? a.thumbnailUrl || null : null,
		expiresAt: a.url ? a.expiresAt || null : null
	};
}

/** The scans of one record, images first (the API's own order). */
export function toAttachmentList(record) {
	if (!record || typeof record !== 'object') return [];
	if (Array.isArray(record.attachments)) return record.attachments.map(toItem).filter(Boolean);
	if (record.attachmentDetails) {
		const item = toItem(record.attachmentDetails);
		return item ? [item] : [];
	}
	return [];
}

/**
 * Earliest `expiresAt` (ms) among the given lists, or null when nothing
 * expires. Accepts any nesting of arrays of normalised items.
 */
export function earliestExpiry(lists) {
	let earliest = null;
	const visit = v => {
		if (Array.isArray(v)) {
			v.forEach(visit);
			return;
		}
		const t = v && v.expiresAt ? Date.parse(v.expiresAt) : NaN;
		if (!Number.isNaN(t) && (earliest === null || t < earliest)) earliest = t;
	};
	visit(lists);
	return earliest;
}

/**
 * Earliest `expiresAt` (ms) of any `attachments` list anywhere in a section
 * payload (documents, certificates, endorsements + flight review), or null.
 */
export function payloadExpiry(payload) {
	const lists = [];
	const visit = v => {
		if (Array.isArray(v)) {
			v.forEach(visit);
		} else if (v && typeof v === 'object') {
			Object.keys(v).forEach(k => {
				if (k === 'attachments' && Array.isArray(v[k])) lists.push(v[k]);
				else visit(v[k]);
			});
		}
	};
	visit(payload);
	return earliestExpiry(lists);
}

/**
 * How long to wait before re-reading the lists so no link on screen is stale:
 * a minute before the earliest expiry, never negative; null when nothing expires.
 */
export function renewDelayMs(expiresAtMs, now = Date.now()) {
	if (expiresAtMs == null) return null;
	return Math.max(0, expiresAtMs - RENEW_MARGIN_MS - now);
}

/**
 * What the upload endpoints accept (symphony-holdshort AttachmentStorageService,
 * same as legacy Documents.php / Certificates.php). Images and files are
 * separate on the server — images get thumbnails (small/medium/big), files are
 * stored as-is — but one button routes each file by extension, so the user
 * never has to pick.
 */
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png', 'bmp'];
// Documents and certificates. `ptt`/`pttx` are spelled so on the server.
const DOCUMENT_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'rtf', 'csv', 'zip', 'rar', 'tar', 'tiff', 'ptt', 'pttx'];
// Endorsements and flight reviews.
const PDF_ONLY = ['pdf'];

const toAccept = exts => exts.map(e => `.${e}`).join(',');

/**
 * Picker filter + hint per destination; `documents` covers certificates too.
 * `minImageSide`: the server refuses smaller document/certificate images
 * (symphony-holdshort `48a05b91`, both sides).
 */
export const UPLOAD_POLICIES = {
	documents: {
		accept: toAccept([...IMAGE_EXTENSIONS, ...DOCUMENT_FILE_EXTENSIONS]),
		minImageSide: 400,
		hint:
			'Images (jpg, png, gif, bmp) at least 400×400 px, up to 7 MB; PDF, Word, Excel, RTF, CSV, ZIP/RAR/TAR or TIFF up to 10 MB.'
	},
	pdf: {
		accept: toAccept([...IMAGE_EXTENSIONS, ...PDF_ONLY]),
		hint: 'Images (jpg, png, gif, bmp) up to 7 MB; PDF up to 10 MB.'
	}
};

export function uploadKindOf(file) {
	const ext = String((file && file.name) || '')
		.split('.')
		.pop()
		.toLowerCase();
	return IMAGE_EXTENSIONS.includes(ext) ? 'image' : 'file';
}

/** `{width, height}` of an image file, or null when the browser can't read it. */
export function readImageSize(file) {
	return new Promise(resolve => {
		if (typeof URL === 'undefined' || typeof Image === 'undefined') {
			resolve(null);
			return;
		}
		const url = URL.createObjectURL(file);
		const img = new Image();
		const done = size => {
			URL.revokeObjectURL(url);
			resolve(size);
		};
		img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight });
		img.onerror = () => done(null);
		img.src = url;
	});
}

/**
 * Upload several files one after another (legacy's uploader took a batch) and
 * report per file, so one bad file does not lose the rest. With `minImageSide`,
 * an image smaller than that on either side fails here, before the upload;
 * one the browser can't read is left to the server.
 *
 * @param {File[]} files
 * @param {(file: File) => Promise} uploadOne
 * @param {{minImageSide?: number, readSize?: (file: File) => Promise<?{width: number, height: number}>}} [options]
 * @returns {Promise<{done: number, failed: {name: string, error: *}[]}>}
 */
export async function uploadEach(files, uploadOne, { minImageSide = 0, readSize = readImageSize } = {}) {
	const list = Array.isArray(files) ? files.filter(Boolean) : files ? [files] : [];
	const failed = [];
	let done = 0;
	for (const file of list) {
		try {
			if (minImageSide && uploadKindOf(file) === 'image') {
				// eslint-disable-next-line no-await-in-loop
				const size = await readSize(file);
				if (size && (size.width < minImageSide || size.height < minImageSide)) {
					throw new Error(
						`The image is ${size.width}×${size.height} px; it must be at least ${minImageSide}×${minImageSide}.`
					);
				}
			}
			// eslint-disable-next-line no-await-in-loop
			await uploadOne(file);
			done += 1;
		} catch (error) {
			failed.push({ name: file.name, error });
		}
	}
	return { done, failed };
}

/** Toast copy for an `uploadEach` result: `{title, description?, color}`. */
export function uploadSummary({ done, failed }, describeError = e => (e && e.message) || '') {
	const scans = n => (n === 1 ? 'Scan' : `${n} scans`);
	if (!failed.length) return { title: `${scans(done)} attached`, color: 'success' };
	const description = failed.map(f => `${f.name}: ${describeError(f.error)}`).join('; ');
	if (!done) return { title: failed.length === 1 ? 'Upload failed' : 'Uploads failed', description, color: 'danger' };
	return { title: `${scans(done)} attached, ${failed.length} failed`, description, color: 'warning' };
}
